// statementHelpers.ts — Sprint 13. Pure helpers for the Statements
// page: filter-mode option lists + the period-based filter pipeline
// + the page's stat aggregator. No React, no antd, no store.
import dayjs, { Dayjs } from 'dayjs';

/**
 * Sprint 146 (R14-13, ADR-141) — `'all'` is new, and it is the page default.
 *
 * The page defaulted to `'monthly'` at the current month, which drops every
 * statement whose `period` doesn't match — including every statement whose
 * period is `""`, the shape StatementService emits for a fee created through
 * the app's own "Add New Fee" form (`POST /api/fees` never sets `period`).
 * Live, that meant the manager's default view showed **1** statement where the
 * server had returned **11**, hiding the 7 owners carrying real balances, with
 * no option anywhere in the filter bar to see them. A money page whose default
 * view silently omits most of the money is worse than no filter at all, so the
 * default is now "show everything the server sent" and narrowing is opt-in.
 */
export type FilterMode = 'all' | 'monthly' | 'quarterly' | 'range';

export interface StatementFilterArgs {
  mode:            FilterMode;
  selectedMonth:   string;          // 'YYYY-MM'
  selectedQuarter: string;          // 'YYYY-Qn'
  dateRange:       [Dayjs, Dayjs] | null;
  searchText:      string;
  /** Resolved condo id — when set, statements from other condos are dropped. */
  condoId?:        string;
}

export interface StatementsStats {
  count:        number;
  sentCount:    number;
  draftCount:   number;
  totalBalance: number;
}

/** Year × month options for the monthly filter, 2024 → next year. */
export function monthOptions(): Array<{ label: string; value: string }> {
  const opts: Array<{ label: string; value: string }> = [];
  for (let y = 2024; y <= dayjs().year() + 1; y++) {
    for (let m = 1; m <= 12; m++) {
      const d = dayjs(`${y}-${String(m).padStart(2, '0')}-01`);
      opts.push({ label: d.format('MMMM YYYY'), value: d.format('YYYY-MM') });
    }
  }
  return opts;
}

/** Year × quarter options for the quarterly filter. */
export function quarterOptions(): Array<{ label: string; value: string }> {
  const opts: Array<{ label: string; value: string }> = [];
  for (let y = 2024; y <= dayjs().year() + 1; y++) {
    for (let q = 1; q <= 4; q++) {
      opts.push({ label: `Q${q} ${y}`, value: `${y}-Q${q}` });
    }
  }
  return opts;
}

/** Apply condo / period / search filters to the statements list.
 *  Months also match their containing quarter — e.g. selecting "March"
 *  also includes any statement whose period is "Q1". */
export function filterStatements(
  statements: any[],
  owners:     any[],
  args:       StatementFilterArgs,
): any[] {
  let base = [...statements];

  // Condo filter
  if (args.condoId) {
    base = base.filter((s: any) => s.condoId === args.condoId);
  }

  // Period filter — `'all'` deliberately applies none (R14-13).
  if (args.mode === 'monthly') {
    base = base.filter((s: any) => {
      if (!s.period) return false;
      if (s.period === args.selectedMonth) return true;
      const [y, m] = args.selectedMonth.split('-').map(Number);
      const q = Math.ceil(m / 3);
      return s.period === `${y}-Q${q}`;
    });
  } else if (args.mode === 'quarterly') {
    base = base.filter((s: any) => s.period === args.selectedQuarter);
  } else if (args.mode === 'range' && args.dateRange) {
    base = base.filter((s: any) => {
      const d = dayjs(s.generatedDate || s.createdAt);
      return d.isAfter(args.dateRange![0].startOf('day')) &&
             d.isBefore(args.dateRange![1].endOf('day'));
    });
  }

  // Search filter
  if (args.searchText) {
    const q = args.searchText.toLowerCase();
    base = base.filter((s: any) => {
      const owner = owners.find((o: any) => o.id === s.ownerId || o.userId === s.ownerId);
      return (
        owner?.name?.toLowerCase().includes(q) ||
        owner?.unitNumber?.toLowerCase().includes(q) ||
        s.period?.toLowerCase().includes(q)
      );
    });
  }

  return base;
}

/** Stat counters used by the cards row. */
export function summarizeStatements(statements: any[]): StatementsStats {
  const sentCount = statements.filter((s: any) => s.isSent).length;
  return {
    count:        statements.length,
    sentCount,
    draftCount:   statements.length - sentCount,
    totalBalance: statements.reduce((s: number, st: any) => s + (st.currentBalance || 0), 0),
  };
}

/** Resolves the canonical "active period" string from the filter
 *  bag — used for the Send All action and as the "Custom Range"
 *  label in the UI.
 *
 *  Sprint 146 — `'all'` resolves to `null`, meaning "every draft in this
 *  condo, whatever its period". Returning a *string* here would have made
 *  Send All a silent no-op in the new default mode (no statement's period
 *  equals "All Periods"), which is the failure shape this round exists to
 *  stop shipping. */
export function resolveActivePeriod(
  mode:            FilterMode,
  selectedMonth:   string,
  selectedQuarter: string,
): string | null {
  if (mode === 'all')       return null;
  if (mode === 'monthly')   return selectedMonth;
  if (mode === 'quarterly') return selectedQuarter;
  return 'Custom Range';
}
